import { EMessengerEventBusEvents, EMessengerScreenName, IMessengerChatSource } from '@xometry/ui';
import { amplitudeLogger } from 'amplitude';
import { AmplitudeAttributeKey, AmplitudeMessengerEvent, MessengerEntryPoint } from 'collections/amplitudeEvents';
import React from 'react';
import { useStore } from 'stores/RootStore';
import { ESideOverlayType, IMessengerInitialScreenParams } from 'stores/SideOverlay/SideOverlayStore';
import { DeviceTypes, useDeviceType } from 'utils/hooks/useDeviceType';
import { useEmbedded } from 'utils/hooks/useEmbedded';
import { useMessengerEventBus } from 'utils/hooks/useMessengerEventBus';
import { withMessengerReinitLayoutMode } from 'utils/messenger/withMessengerReinitLayoutMode';

interface Params {
  context: IMessengerChatSource;
  entryPoint?: MessengerEntryPoint;
  onClick?: () => void;
}

export const useMessengerButton = (params: Params) => {
  const { context, entryPoint, onClick: onClickProp } = params;
  const { sideOverlayStore, erpMessengerLayoutStore } = useStore();

  const {
    openSideOverlay,
    context: { type: sideOverlayType },
    clearMessengerLayoutPresentationOverride,
    patchMessengerOverlay,
  } = sideOverlayStore;

  const { sendEvent } = useMessengerEventBus();
  const { isEmbedded } = useEmbedded();
  const deviceType = useDeviceType();

  const isMobile = deviceType === DeviceTypes.MOBILE && !isEmbedded;

  return (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!context.sourceId || context.sourceId === 'undefined') return;

    e.preventDefault();
    e.stopPropagation();

    const messengerContext: IMessengerInitialScreenParams = {
      initialScreen: {
        name: EMessengerScreenName.CHAT,
        params: { chatSource: context },
      },
    };

    if (sideOverlayType === ESideOverlayType.Messenger) {
      clearMessengerLayoutPresentationOverride();
      patchMessengerOverlay(messengerContext);

      sendEvent?.(
        EMessengerEventBusEvents.REINITIALIZE_MC,
        withMessengerReinitLayoutMode({
          userPreference: erpMessengerLayoutStore.userErpMessengerLayoutMode,
          isMobile,
          isEmbedded,
          overlayContext: sideOverlayStore.context,
          payload: messengerContext,
        }),
      );
    } else {
      openSideOverlay({
        type: ESideOverlayType.Messenger,
        ...messengerContext,
      });
    }

    if (entryPoint) {
      amplitudeLogger({
        event: AmplitudeMessengerEvent.MESSENGER_OPENED,
        attrs: {
          [AmplitudeAttributeKey.ENTRY_POINT]: entryPoint,
          sourceType: context.sourceType,
          sourceId: context.sourceId,
          mode: erpMessengerLayoutStore.userErpMessengerLayoutMode,
        },
      });
    }

    if (typeof onClickProp === 'function') {
      onClickProp();
    }
  };
};
